/*  MegaMenu — "Products" (Guards & Retainer) dropdown in the site header.
    ────────────────────────────────────────────────────────────────
    iter100.122 — Fully CMS-driven. Each of the 4 product cards pulls
    its title / tagline / image from `db.cms_content` (page `nav_products`).
    Editable from Admin → Landing Page → Content → nav_products tab.

    Behaviour:
      • Desktop: hover / focus / click on the "Products" nav item opens
        the panel below the header. Panel closes on outside click, on
        Escape, or on selecting a card.
      • Mobile: rendered as a stacked list inside the hamburger sheet
        (see MobileProductsSection below).

    Copy strategy:
      • NO "Shop Now" / cart CTAs per user's iter100.122 spec. Each
        card's CTA is a marketing / doctor-pathway link only.
      • Retainer card links to the existing `/infinite` page. Other
        three go to `/products/<slug>` marketing pages with an
        enquiry form.
*/
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    CaretDown, Moon, Bed, Barbell, Sparkle, ArrowRight, Question,
} from "@phosphor-icons/react";
import { useCms } from "@/lib/useCms";


/* ─── Product spec — order + slug + route + icon ─────────────────── */
export const PRODUCTS = [
    {
        slug: "night_guard",
        route: "/mouthguards-and-retainer/night-guard",
        icon: Moon,
        tint: "from-indigo-100 to-slate-100",
        iconTint: "text-indigo-600",
    },
    {
        slug: "anti_snoring",
        route: "/mouthguards-and-retainer/anti-snoring",
        icon: Bed,
        tint: "from-sky-100 to-cyan-100",
        iconTint: "text-sky-600",
    },
    {
        slug: "sports_guard",
        route: "/mouthguards-and-retainer/sports-guard",
        icon: Barbell,
        tint: "from-amber-100 to-rose-100",
        iconTint: "text-amber-600",
    },
    {
        slug: "retainer",
        route: "/mouthguards-and-retainer/retainer",
        icon: Sparkle,
        tint: "from-emerald-100 to-teal-100",
        iconTint: "text-emerald-600",
    },
];


/* ─── Desktop trigger + dropdown panel ───────────────────────────── */
export function DesktopProductsMenu() {
    const { t } = useCms("nav_products");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const closeTimer = useRef(null);

    // Close on outside click.
    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // Close on Escape.
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    // Hover intent: brief delay before closing so the pointer can
    // travel from the trigger into the panel without collapsing it.
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 140);
    };
    const cancelClose = () => {
        if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    };

    return (
        <div
            ref={wrapRef}
            className="relative"
            onMouseEnter={() => { cancelClose(); setOpen(true); }}
            onMouseLeave={scheduleClose}
            data-testid="mega-menu-wrap"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                onFocus={() => setOpen(true)}
                aria-expanded={open}
                aria-haspopup="true"
                data-testid="mega-menu-trigger"
                className={`group relative text-sm font-medium px-3 py-2 whitespace-nowrap rounded-md transition-colors inline-flex items-center gap-1 ${
                    open ? "text-primary" : "text-foreground hover:text-primary"
                }`}
            >
                {t("menu_label", "Mouthguards & Retainer")}
                <CaretDown
                    size={12}
                    weight="bold"
                    className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
                <span
                    className={`absolute left-3 right-6 -bottom-0.5 h-0.5 bg-primary rounded-full origin-left transition-transform duration-300 ease-out ${
                        open ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                    aria-hidden="true"
                />
            </button>

            {open && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-50"
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    data-testid="mega-menu-panel"
                >
                    <div className="w-[min(92vw,880px)] rounded-2xl border border-border bg-card shadow-2xl shadow-primary/10 overflow-hidden">
                        <div className="p-6 pb-4 border-b border-border/60 bg-gradient-to-br from-primary/[0.04] to-transparent">
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary" data-testid="mega-menu-eyebrow">
                                Mouthguards & Retainer
                            </div>
                            <div className="font-display text-2xl font-bold tracking-tight mt-0.5" data-testid="mega-menu-heading">
                                {t("menu_heading", "Mouthguards & Retainer")}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 max-w-xl leading-relaxed" data-testid="mega-menu-tagline">
                                {t("menu_tagline", "Precision-crafted appliances for every stage of your smile.")}
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-1 p-3" data-testid="mega-menu-grid">
                            {PRODUCTS.map((p) => (
                                <ProductCard
                                    key={p.slug}
                                    p={p}
                                    t={t}
                                    onSelect={() => setOpen(false)}
                                />
                            ))}
                        </div>

                        <div className="px-6 py-4 border-t border-border/60 bg-secondary/30 flex items-center justify-between flex-wrap gap-3" data-testid="mega-menu-footer">
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Question size={14} weight="duotone" className="text-primary" />
                                <span>{t("footer_prompt", "Not sure which is right for you?")}</span>
                            </div>
                            <Link
                                to="/contact"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                                data-testid="mega-menu-footer-cta"
                            >
                                {t("footer_cta", "Talk to a smile expert")}
                                <ArrowRight size={11} weight="bold" />
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function ProductCard({ p, t, onSelect }) {
    const Icon = p.icon;
    const title = t(`${p.slug}.title`, "");
    const tagline = t(`${p.slug}.tagline`, "");
    const blurb = t(`${p.slug}.blurb`, "");
    const imageUrl = t(`${p.slug}.image_url`, "");

    return (
        <Link
            to={p.route}
            onClick={onSelect}
            data-testid={`mega-menu-card-${p.slug}`}
            className="group flex gap-3 p-3 rounded-xl border border-transparent hover:border-primary/25 hover:bg-primary/[0.04] hover:shadow-sm transition-all"
        >
            <div
                className={`w-16 h-16 shrink-0 rounded-xl overflow-hidden bg-gradient-to-br ${p.tint} grid place-items-center relative ring-1 ring-black/5`}
                data-testid={`mega-menu-card-image-${p.slug}`}
            >
                {imageUrl ? (
                    <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
                ) : (
                    <Icon size={26} weight="duotone" className={p.iconTint} />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                    <div className="font-display font-bold text-sm text-foreground truncate" data-testid={`mega-menu-card-title-${p.slug}`}>
                        {title || p.slug.replace("_", " ")}
                    </div>
                </div>
                {tagline && (
                    <div className="text-[11px] font-semibold text-primary mt-0.5" data-testid={`mega-menu-card-tagline-${p.slug}`}>
                        {tagline}
                    </div>
                )}
                {blurb && (
                    <div className="text-[11.5px] text-muted-foreground mt-1 leading-snug line-clamp-2">
                        {blurb}
                    </div>
                )}
                <div className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-primary/80 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    Explore
                    <ArrowRight size={9} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
                </div>
            </div>
        </Link>
    );
}


/* ─── Mobile stacked list — dropped into the hamburger sheet ─────── */
export function MobileProductsSection({ onNavigate }) {
    const { t } = useCms("nav_products");
    const [open, setOpen] = useState(false);
    return (
        <div className="border border-border rounded-xl overflow-hidden" data-testid="mobile-products-section">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="mobile-products-toggle"
                className="w-full flex items-center justify-between px-3 py-2.5 text-base font-medium bg-secondary/40"
                aria-expanded={open}
            >
                <span>{t("menu_label", "Mouthguards & Retainer")}</span>
                <CaretDown size={14} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="p-2 space-y-1 bg-card">
                    {PRODUCTS.map((p) => {
                        const Icon = p.icon;
                        const title = t(`${p.slug}.title`, "");
                        return (
                            <Link
                                key={p.slug}
                                to={p.route}
                                onClick={() => { setOpen(false); onNavigate?.(); }}
                                data-testid={`mobile-products-link-${p.slug}`}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-primary/5 transition-colors"
                            >
                                <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${p.tint} grid place-items-center`}>
                                    <Icon size={16} weight="duotone" className={p.iconTint} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-foreground">{title || p.slug.replace("_", " ")}</div>
                                    <div className="text-[10.5px] text-muted-foreground truncate">
                                        {t(`${p.slug}.tagline`, "")}
                                    </div>
                                </div>
                                <ArrowRight size={12} weight="bold" className="text-primary shrink-0" />
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


export default DesktopProductsMenu;
